import {
  lazy,
  Suspense,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type MutableRefObject,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import { createPortal } from 'react-dom'

import { SunriseOverlay } from '@/components/sunrise-overlay'
import { MOBILE_NAV_RISE_TIMINGS } from '@/hooks/use-sunrise-overlay-timeline'
import { contactChannels } from '@/data/morning-landing-content'
import {
  queuePageHashScroll,
  syncPageHash,
} from '@/lib/scroll-to-page-hash'
import { cn } from '@/lib/utils'

const QuickContactForm = lazy(() =>
  import('@/components/quick-contact-form').then((m) => ({
    default: m.QuickContactForm,
  })),
)

const headingFontClass = 'font-heading-outfit'
const CONTACT_SHEET_ANIMATION_MS = 300

const homeLink = { href: '/#top', label: 'Home' } as const

export type MobileNavLink = {
  href: string
  label: string
}

type MobileNavOverlayProps = {
  id?: string
  links: readonly MobileNavLink[]
  onClosed: () => void
  /** Site header menu button uses this to dismiss while the overlay sits behind the nav. */
  closeRef?: MutableRefObject<(() => void) | null>
  /** When true on mount (or when flipped on), show the contact sheet immediately. */
  contactOpen?: boolean
  onContactOpenChange?: (open: boolean) => void
}

function isContactHref(href: string) {
  return href === '#contact' || href === '/#contact'
}

function isTopHref(href: string) {
  return href === '#top' || href === '/#top'
}

export function MobileNavOverlay({
  id,
  links,
  onClosed,
  closeRef,
  contactOpen: contactOpenProp,
  onContactOpenChange,
}: MobileNavOverlayProps) {
  const menuLinks = [homeLink, ...links]
  const isContactControlled = contactOpenProp !== undefined
  const [contactOpenUncontrolled, setContactOpenUncontrolled] = useState(
    () => contactOpenProp ?? false,
  )
  const contactOpen = isContactControlled
    ? Boolean(contactOpenProp)
    : contactOpenUncontrolled
  const setContactOpen = (open: boolean) => {
    if (!isContactControlled) {
      setContactOpenUncontrolled(open)
    }
    onContactOpenChange?.(open)
  }
  const [contactMounted, setContactMounted] = useState(() => contactOpen)
  const [overlayRoot, setOverlayRoot] = useState<HTMLElement | null>(null)
  const [headerOffsetPx, setHeaderOffsetPx] = useState(0)
  const dismissDragStartY = useRef<number | null>(null)

  useLayoutEffect(() => {
    const root = id
      ? document.getElementById(id)
      : document.querySelector<HTMLElement>('.sunrise-overlay')
    setOverlayRoot(root)
  }, [id])

  useLayoutEffect(() => {
    const bar = document.querySelector<HTMLElement>('[data-site-header-bar]')
    if (!bar) return

    const updateOffset = () => {
      setHeaderOffsetPx(Math.ceil(bar.getBoundingClientRect().bottom))
    }

    updateOffset()
    const observer = new ResizeObserver(updateOffset)
    observer.observe(bar)
    window.addEventListener('resize', updateOffset)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', updateOffset)
    }
  }, [])

  useLayoutEffect(() => {
    if (contactOpen) {
      setContactMounted(true)
      return
    }

    if (!contactMounted) return

    const closeTimer = setTimeout(() => {
      setContactMounted(false)
    }, CONTACT_SHEET_ANIMATION_MS)

    return () => clearTimeout(closeTimer)
  }, [contactOpen, contactMounted])

  // Close the sheet before the menu when Escape is pressed.
  useEffect(() => {
    if (!contactOpen) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      event.stopImmediatePropagation()
      setContactOpen(false)
    }

    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [contactOpen])

  useEffect(() => {
    if (!contactOpen || !contactMounted || !overlayRoot) return

    const firstField = overlayRoot.querySelector<HTMLElement>(
      '[data-mobile-contact-sheet] input, [data-mobile-contact-sheet] textarea',
    )
    firstField?.focus()
  }, [contactOpen, contactMounted, overlayRoot])

  return (
    <SunriseOverlay
      id={id}
      onClosed={onClosed}
      labelledBy="mobile-nav-heading"
      closeAriaLabel="Close menu"
      // Contact sheet is portaled outside the panel; skip nav autofocus when it is open.
      autoFocusSelector={
        contactOpen ? '[data-mobile-contact-autofocus]' : '[data-mobile-nav-link]'
      }
      timings={MOBILE_NAV_RISE_TIMINGS}
      hideHeader
      closeRef={closeRef}
      // Keep in the header’s stacking context (page root uses `isolate`) under the nav bar.
      disablePortal
      className="z-40"
      panelClassName="flex min-h-0 max-w-lg flex-1 flex-col"
    >
      {({ handleClose }) => (
        <div className="flex min-h-full flex-1 flex-col">
          <div className="sunrise-overlay__content get-in-touch-overlay__content flex min-h-0 flex-1 flex-col">
            <h2 id="mobile-nav-heading" className="sr-only">
              Menu
            </h2>
            <nav
              className={cn(
                '-mx-6 inline-flex w-[calc(100%+3rem)] max-w-none shrink-0 flex-col items-stretch divide-y divide-paper/20 overflow-hidden rounded-none border-x-0 border-t-0 border-b border-paper/25 bg-paper/10 text-base font-medium text-paper md:mx-auto md:mt-6 md:w-full md:max-w-lg md:rounded-xl md:border',
                headingFontClass,
              )}
              aria-label="Primary"
            >
              {menuLinks.map((link) => {
                const itemClassName =
                  'flex flex-1 flex-col px-5 py-3.5 text-center transition-colors hover:bg-paper/15 hover:text-paper/80 sm:py-4'

                if (isContactHref(link.href)) {
                  return (
                    <button
                      key={link.href}
                      type="button"
                      data-mobile-nav-link=""
                      aria-expanded={contactOpen}
                      aria-controls="mobile-menu-contact-sheet"
                      onClick={() => setContactOpen(true)}
                      className={itemClassName}
                    >
                      {link.label}
                    </button>
                  )
                }

                return (
                  <a
                    key={link.href}
                    href={link.href}
                    data-mobile-nav-link=""
                    onClick={(event) => {
                      if (isTopHref(link.href)) {
                        event.preventDefault()
                        handleClose()
                        window.scrollTo({ top: 0, behavior: 'smooth' })
                        return
                      }
                      event.preventDefault()
                      queuePageHashScroll(link.href)
                      syncPageHash(link.href)
                      handleClose()
                    }}
                    className={itemClassName}
                  >
                    {link.label}
                  </a>
                )
              })}
            </nav>

            <div
              className={cn(
                '-mx-6 mt-auto inline-flex w-[calc(100%+3rem)] max-w-none shrink-0 flex-col items-stretch divide-y divide-paper/20 overflow-hidden rounded-none border-x-0 border-y border-paper/25 bg-paper/10 md:mx-auto md:w-full md:max-w-lg md:rounded-xl md:border md:flex-row md:divide-x md:divide-y-0',
                headingFontClass,
              )}
            >
              <a
                href={contactChannels.phone.href}
                className={cn(
                  'flex flex-1 flex-col px-5 py-3.5 text-center font-medium text-paper transition-colors hover:bg-paper/15 hover:text-paper/80 sm:py-4',
                  headingFontClass,
                )}
              >
                <span className="text-sm font-medium tracking-wide text-paper/55 uppercase sm:text-xs">
                  Phone
                </span>
                <span className="mt-1 text-base">
                  {contactChannels.phone.display}
                </span>
              </a>
              <a
                href={contactChannels.email.href}
                className={cn(
                  'flex flex-1 flex-col px-5 py-3.5 text-center font-medium text-paper transition-colors hover:bg-paper/15 hover:text-paper/80 sm:py-4',
                  headingFontClass,
                )}
              >
                <span className="text-sm font-medium tracking-wide text-paper/55 uppercase sm:text-xs">
                  Email
                </span>
                <span className="mt-1 text-base">
                  {contactChannels.email.display}
                </span>
              </a>
            </div>
          </div>

          {contactMounted && overlayRoot
            ? createPortal(
                <div
                  id="mobile-menu-contact-sheet"
                  data-mobile-contact-sheet=""
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="mobile-menu-contact-heading"
                  data-state={contactOpen ? 'open' : 'closed'}
                  className={cn(
                    'absolute inset-0 z-20 overflow-y-auto bg-ember text-paper duration-300 dark:bg-ember-deep',
                    contactOpen
                      ? 'animate-in slide-in-from-bottom fade-in-0 ease-out'
                      : 'animate-out slide-out-to-bottom fade-out-0 ease-in',
                  )}
                >
                  <div
                    className="mx-auto flex min-h-full w-full max-w-lg flex-col px-6"
                    style={{ paddingTop: headerOffsetPx }}
                  >
                    <div
                      className={cn(
                        '-mx-6 flex min-h-0 w-[calc(100%+3rem)] max-w-none flex-1 flex-col rounded-none border-0 bg-ember px-5 pt-3 pb-8 md:mx-0 md:w-full dark:bg-ember-deep',
                        headingFontClass,
                      )}
                    >
                      <button
                        type="button"
                        aria-label="Back to menu"
                        title="Swipe or tap to return to menu"
                        onClick={() => setContactOpen(false)}
                        onPointerDown={(
                          event: ReactPointerEvent<HTMLButtonElement>,
                        ) => {
                          dismissDragStartY.current = event.clientY
                          event.currentTarget.setPointerCapture(event.pointerId)
                        }}
                        onPointerUp={(
                          event: ReactPointerEvent<HTMLButtonElement>,
                        ) => {
                          const startY = dismissDragStartY.current
                          dismissDragStartY.current = null
                          if (startY == null) return
                          // Drag down on the handle to dismiss, matching the affordance.
                          if (event.clientY - startY > 36) {
                            setContactOpen(false)
                          }
                        }}
                        onPointerCancel={() => {
                          dismissDragStartY.current = null
                        }}
                        className="group mx-auto mb-4 flex h-8 w-full shrink-0 items-center justify-center rounded-full outline-none focus-visible:ring-3 focus-visible:ring-paper/30"
                      >
                        <span
                          aria-hidden
                          className="h-1 w-10 rounded-full bg-paper/35 transition-colors group-hover:bg-paper/55"
                        />
                      </button>

                      <div className="shrink-0 text-center">
                        <p
                          id="mobile-menu-contact-heading"
                          className={cn(
                            'text-2xl font-semibold tracking-tight text-balance text-paper sm:text-3xl',
                            headingFontClass,
                          )}
                        >
                          Get in touch
                        </p>
                        <p
                          className={cn(
                            'mt-2 text-sm text-pretty text-paper/50',
                            headingFontClass,
                          )}
                        >
                          Tell us about your next project.
                        </p>
                      </div>
                      <div className="mt-5 flex flex-1 flex-col">
                        <Suspense fallback={null}>
                          <QuickContactForm
                            idPrefix="mobile-menu-contact"
                            variant="overlay"
                            overlayStyle="section"
                          />
                        </Suspense>
                      </div>
                    </div>
                  </div>
                </div>,
                overlayRoot,
              )
            : null}
        </div>
      )}
    </SunriseOverlay>
  )
}
